import { Injectable } from '@angular/core';
import { interval, Observable, repeat, takeWhile, tap } from 'rxjs';

export type CacheItem<T> = CacheItemInflight<T> | CacheItemReady<T>;
interface CacheItemInflight<T> {
  observable: Observable<T>;
  time: number;
  status: 'inflight';
  item?: never;
}
interface CacheItemReady<T> {
  item: T;
  time: number;
  status: 'ready';
}

@Injectable({
  providedIn: 'root',
})
export class HttpCacheService {
  private readonly PURGE_CACHE_IN_MINUTES = 5;
  private readonly cache = new Map<string, CacheItem<any>>();
  cacheVerificationWatcher$ = interval(1000)
    .pipe(
      takeWhile(() => 0 !== this.cache.size),
      tap(() => this.verifyCache()),
      repeat(),
    )
    .subscribe();

  clearCache(nameSpace: string) {
    this.cache.forEach((value, key) => {
      const regex = new RegExp(`^${nameSpace}_`, 'i');
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    });
  }

  isCacheExpired(value: any) {
    return (
      value.time <
      new Date(new Date().getTime() - this.PURGE_CACHE_IN_MINUTES * 1000 * 60)
    );
  }

  verifyCache() {
    this.cache.forEach((value, key) => {
      if (this.isCacheExpired(value)) {
        this.cache.delete(key);
      }
    });
  }

  startCache<T>(key: string, observable: Observable<T>) {
    const item: CacheItem<T> = {
      observable,
      status: 'inflight',
      time: Date.now(),
    };
    this.cache.set(key, item);
  }

  updateItem(key: string, item: any): void {
    const prevValue = this.cache.get(key);
    this.cache.set(key, {
      ...prevValue,
      item,
      status: 'ready',
      time: Date.now(),
    });
  }

  getCache<T>(key: string): CacheItem<T> {
    return this.cache.get(key);
  }
}
